import React, { useState } from 'react';
import { X, Search, Eye, EyeOff, Loader2, UtensilsCrossed } from 'lucide-react';
import { useMenuCategories, useMenuItems } from '../../lib/menu-hooks';
import { cn } from '../../lib/utils';
interface MenuToggleDrawerProps {
  restaurantId: string;
  isOpen: boolean;
  onClose: () => void;
}
export function MenuToggleDrawer({
  restaurantId,
  isOpen,
  onClose
}: MenuToggleDrawerProps) {
  const {
    categories,
    loading: catLoading
  } = useMenuCategories(restaurantId);
  const {
    items,
    loading: itemsLoading,
    toggleItem
  } = useMenuItems(restaurantId);
  const [searchTerm, setSearchTerm] = useState('');
  const [toggling, setToggling] = useState<Set<string>>(new Set());
  if (!isOpen) return null;
  const handleToggle = async (id: string, currentStatus: boolean) => {
    setToggling(prev => new Set(prev).add(id));
    await toggleItem(id, !currentStatus);
    setToggling(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };
  const isLoading = catLoading || itemsLoading;
  const filteredItems = items.filter(i => i.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const grouped = categories.map(cat => ({
    category: cat,
    items: filteredItems.filter(i => i.category_id === cat.id)
  })).filter(g => g.items.length > 0);
  const uncategorized = filteredItems.filter(i => !categories.find(c => c.id === i.category_id));
  if (uncategorized.length > 0) {
    grouped.push({
      category: {
        id: 'uncategorized',
        name: 'Otros'
      } as any,
      items: uncategorized
    });
  }
  return <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] transition-opacity" onClick={onClose} data-id="element-694" />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 w-full sm:w-[400px] bg-[#141414] border-l border-white/10 shadow-2xl z-[101] flex flex-col transform transition-transform duration-300" data-id="element-695">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 shrink-0" data-id="element-696">
          <div className="flex items-center gap-3" data-id="element-697">
            <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center" data-id="element-698">
              <UtensilsCrossed className="w-5 h-5 text-white/70" data-id="element-699" />
            </div>
            <div data-id="element-700">
              <h2 className="text-lg font-bold text-white leading-tight" data-id="element-701">
                Disponibilidad
              </h2>
              <p className="text-xs text-white/40" data-id="element-702">Ocultar/mostrar productos</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-colors" data-id="element-703">
            <X className="w-5 h-5" data-id="element-704" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-white/5 shrink-0" data-id="element-705">
          <div className="relative" data-id="element-706">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" data-id="element-707" />
            <input type="text" placeholder="Buscar producto..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500/50 transition-colors" data-id="element-708" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar" data-id="element-709">
          {isLoading ? <div className="flex flex-col items-center justify-center h-40 gap-3 text-white/30" data-id="element-710">
              <Loader2 className="w-6 h-6 animate-spin" data-id="element-711" />
              <span className="text-sm" data-id="element-712">Cargando menú...</span>
            </div> : grouped.length === 0 ? <div className="text-center py-10 text-white/40 text-sm" data-id="element-713">
              No se encontraron productos
            </div> : grouped.map(group => <div key={group.category.id} className="space-y-2" data-id="element-714">
                <h3 className="text-xs font-bold text-white/40 uppercase tracking-widest px-1" data-id="element-715">
                  {group.category.name}
                </h3>
                <div className="bg-[#0B0B0B] border border-white/5 rounded-2xl overflow-hidden divide-y divide-white/5" data-id="element-716">
                  {group.items.map(item => {
              const isToggling = toggling.has(item.id);
              return <div key={item.id} className={cn('flex items-center justify-between p-3 transition-colors', !item.is_active && 'bg-red-500/5')} data-id="element-717">
                        <div className="flex-1 min-w-0 pr-4" data-id="element-718">
                          <p className={cn('text-sm font-medium truncate transition-colors', item.is_active ? 'text-white/90' : 'text-white/40 line-through')} data-id="element-719">
                            {item.name}
                          </p>
                        </div>
                        <button onClick={() => handleToggle(item.id, item.is_active)} disabled={isToggling} className={cn('shrink-0 flex items-center justify-center w-10 h-10 rounded-xl transition-all active:scale-95', isToggling && 'opacity-50 cursor-not-allowed', item.is_active ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20' : 'bg-red-500/10 text-red-400 hover:bg-red-500/20')} title={item.is_active ? 'Ocultar producto' : 'Mostrar producto'} data-id="element-720">
                          {isToggling ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-721" /> : item.is_active ? <Eye className="w-5 h-5" data-id="element-722" /> : <EyeOff className="w-5 h-5" data-id="element-723" />}
                        </button>
                      </div>;
            })}
                </div>
              </div>)}
        </div>
      </div>
    </>;
}